import { useState, useEffect } from 'react';
import { api, MarketMoodPeriod, SentimentMarket, MarketMoodStats } from '../lib/api';

const MOOD_OPTIONS: { value: SentimentMarket; label: string; color: string }[] = [
  { value: 'panic', label: 'Panic', color: '#c0392b' },
  { value: 'bear', label: 'Bear', color: '#e67e22' },
  { value: 'bull', label: 'Bull', color: '#3498db' },
  { value: 'euphoric', label: 'Euphoric', color: '#1a5276' },
];

const getMoodColor = (mood: SentimentMarket): string => {
  return MOOD_OPTIONS.find((m) => m.value === mood)?.color || '#6c757d';
};

const getMoodLabel = (mood: SentimentMarket | 'unknown'): string => {
  if (mood === 'unknown') return 'Unknown';
  return MOOD_OPTIONS.find((m) => m.value === mood)?.label || mood;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return 'Ongoing';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export function MarketMood() {
  const [periods, setPeriods] = useState<MarketMoodPeriod[]>([]);
  const [currentPeriod, setCurrentPeriod] = useState<MarketMoodPeriod | null>(null);
  const [stats, setStats] = useState<MarketMoodStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<MarketMoodPeriod | null>(null);
  const [retagging, setRetagging] = useState(false);

  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    mood: 'bull' as SentimentMarket,
    notes: '',
    set_by: 'santiago',
    ongoing: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [periodsResult, currentResult, statsResult] = await Promise.all([
        api.listMarketMoodPeriods(),
        api.getCurrentMarketMoodPeriod(),
        api.getMarketMoodStats(),
      ]);
      setPeriods(periodsResult.periods);
      setCurrentPeriod(currentResult.current_period);
      setStats(statsResult);
    } catch (err) {
      setError('Failed to load market mood data');
    } finally {
      setLoading(false);
    }
  };

  const handleRunRetag = async () => {
    setRetagging(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.runMarketMoodTagging();
      setSuccess(
        `Retagging complete: ${result.tagged_with_mood} insights tagged with mood, ${result.tagged_unknown} tagged as unknown (${result.duration_ms}ms)`
      );
      // Refresh stats
      const statsResult = await api.getMarketMoodStats();
      setStats(statsResult);
    } catch (err) {
      setError('Failed to run tagging job');
    } finally {
      setRetagging(false);
    }
  };

  const openAddModal = () => {
    setFormData({
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      mood: 'bull',
      notes: '',
      set_by: 'santiago',
      ongoing: true,
    });
    setEditingPeriod(null);
    setShowAddModal(true);
  };

  const openEditModal = (period: MarketMoodPeriod) => {
    setFormData({
      start_date: period.start_date,
      end_date: period.end_date || '',
      mood: period.mood,
      notes: period.notes || '',
      set_by: period.set_by,
      ongoing: period.end_date === null,
    });
    setEditingPeriod(period);
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingPeriod(null);
  };

  const handleSubmit = async () => {
    setError('');
    try {
      const data = {
        start_date: formData.start_date,
        end_date: formData.ongoing ? null : formData.end_date || null,
        mood: formData.mood,
        notes: formData.notes || undefined,
        set_by: formData.set_by,
      };

      if (editingPeriod) {
        await api.updateMarketMoodPeriod(editingPeriod.period_id, data);
        setSuccess('Period updated successfully');
      } else {
        await api.createMarketMoodPeriod(data);
        setSuccess('Period created successfully');
      }
      closeModal();
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save period';
      setError(message);
    }
  };

  const handleDelete = async (period: MarketMoodPeriod) => {
    if (!confirm(`Delete the ${getMoodLabel(period.mood)} period (${formatDate(period.start_date)} - ${formatDate(period.end_date)})?`)) {
      return;
    }
    setError('');
    try {
      await api.deleteMarketMoodPeriod(period.period_id);
      setSuccess('Period deleted successfully');
      loadData();
    } catch (err) {
      setError('Failed to delete period');
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Market Mood</h1>
        </div>
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Market Mood</h1>
        <p className="page-subtitle">
          Manage client-perceived market mood periods for insight analysis
        </p>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      {/* Current Mood Banner */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3>Current Market Mood</h3>
        </div>
        <div className="card-body">
          {currentPeriod ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div
                className="mood-badge large"
                style={{ backgroundColor: getMoodColor(currentPeriod.mood) }}
              >
                {getMoodLabel(currentPeriod.mood)}
              </div>
              <div>
                <div style={{ fontWeight: 500 }}>
                  Since {formatDate(currentPeriod.start_date)}
                  {currentPeriod.end_date && ` until ${formatDate(currentPeriod.end_date)}`}
                </div>
                {currentPeriod.notes && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    {currentPeriod.notes}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted)' }}>
              No current mood period defined. Insights will be tagged as &quot;unknown&quot;.
            </div>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3>Tagging Statistics</h3>
            <button
              className="btn btn-primary btn-small"
              onClick={handleRunRetag}
              disabled={retagging}
            >
              {retagging ? 'Retagging...' : 'Run Retag Now'}
            </button>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              {(stats.insights?.mood_distribution ?? []).map((item) => (
                <div key={item.market_mood} style={{ textAlign: 'center' }}>
                  <div
                    className="mood-badge small"
                    style={{
                      backgroundColor:
                        item.market_mood === 'unknown'
                          ? '#6c757d'
                          : getMoodColor(item.market_mood as SentimentMarket),
                    }}
                  >
                    {getMoodLabel(item.market_mood as SentimentMarket | 'unknown')}
                  </div>
                  <div style={{ fontWeight: 600, marginTop: '0.5rem' }}>{item.count}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>insights</div>
                </div>
              ))}
            </div>
            {stats.insights?.date_range?.earliest && stats.insights?.date_range?.latest && (
              <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                Insight date range: {formatDate(stats.insights.date_range.earliest)} to{' '}
                {formatDate(stats.insights.date_range.latest)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline Visualization */}
      {periods.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3>Mood Timeline</h3>
          </div>
          <div className="card-body">
            <div className="mood-timeline">
              {periods.map((period) => (
                <div
                  key={period.period_id}
                  className="mood-timeline-segment"
                  style={{ backgroundColor: getMoodColor(period.mood) }}
                  title={`${getMoodLabel(period.mood)}: ${formatDate(period.start_date)} - ${formatDate(period.end_date)}`}
                >
                  <span className="mood-timeline-label">{getMoodLabel(period.mood)}</span>
                  <span className="mood-timeline-dates">
                    {formatDate(period.start_date)}
                    {period.end_date ? ` - ${formatDate(period.end_date)}` : ' (ongoing)'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Periods List */}
      <div className="card">
        <div className="card-header">
          <h3>Mood Periods</h3>
          <button className="btn btn-primary btn-small" onClick={openAddModal}>
            + Add Period
          </button>
        </div>
        <div className="card-body">
          {periods.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
              No mood periods defined yet. Add your first period to start tagging insights.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Mood</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Notes</th>
                  <th>Set By</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((period) => (
                  <tr key={period.period_id}>
                    <td>
                      <div
                        className="mood-badge small"
                        style={{ backgroundColor: getMoodColor(period.mood) }}
                      >
                        {getMoodLabel(period.mood)}
                      </div>
                    </td>
                    <td>{formatDate(period.start_date)}</td>
                    <td>
                      {period.end_date ? (
                        formatDate(period.end_date)
                      ) : (
                        <span className="badge badge-green">Ongoing</span>
                      )}
                    </td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {period.notes || '-'}
                    </td>
                    <td>{period.set_by}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-ghost btn-small"
                          onClick={() => openEditModal(period)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-ghost btn-small btn-danger"
                          onClick={() => handleDelete(period)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPeriod ? 'Edit Mood Period' : 'Add Mood Period'}</h3>
              <button className="modal-close" onClick={closeModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Mood</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {MOOD_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      className={`mood-select-btn ${formData.mood === option.value ? 'selected' : ''}`}
                      style={{
                        backgroundColor:
                          formData.mood === option.value ? option.color : 'transparent',
                        borderColor: option.color,
                        color: formData.mood === option.value ? 'white' : option.color,
                      }}
                      onClick={() => setFormData({ ...formData, mood: option.value })}
                      type="button"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  className="input"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={formData.ongoing}
                    onChange={(e) => setFormData({ ...formData, ongoing: e.target.checked })}
                  />
                  Ongoing (no end date)
                </label>
              </div>

              {!formData.ongoing && (
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="input"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    min={formData.start_date}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  className="input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="e.g., SVB collapse fears, post-election rally..."
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Set By</label>
                <input
                  type="text"
                  className="input"
                  value={formData.set_by}
                  onChange={(e) => setFormData({ ...formData, set_by: e.target.value })}
                  placeholder="e.g., santiago"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!formData.start_date || !formData.set_by}
              >
                {editingPeriod ? 'Save Changes' : 'Create Period'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
